import { type FC } from 'react';
import { ArrowDownNarrowWide, ArrowUpNarrowWide, ListFilter } from 'lucide-react';
import { type CommentResolutionFilterOption, type CommentVisibilityFilterOption } from '@/types/comments';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { FilterCommentsMenu, SortCommentsMenu } from '@/components/menu-items/node-menu-items';
import { ToolbarAction } from '@/components/inspector/uploads-tree-selector-dialog';
import { MenuProvider, dropdownPrimitives } from '@/components/menu-renderer';
import { SortOrderOptions, type SortOrderValues } from '@/types/sort';
import { cn } from '@/lib/utils';
import { useTranslations } from 'next-intl';

export const CommentsToolbar: FC<{
  comments: Array<any>;
  sortOrder: SortOrderOptions;
  sortValue: SortOrderValues;
  onSortOrderChange: (order: SortOrderOptions) => void;
  onSortValueChange: (value: SortOrderValues) => void;
  resolutionFilter?: CommentResolutionFilterOption;
  onResolutionFilterChange: (value: CommentResolutionFilterOption | undefined) => void;
  visibilityFilter?: CommentVisibilityFilterOption;
  onVisibilityFilterChange: (value: CommentVisibilityFilterOption | undefined) => void;
  canFilterByVisibility?: boolean;
}> = ({
  comments,
  sortOrder,
  sortValue,
  onSortOrderChange,
  onSortValueChange,
  resolutionFilter,
  onResolutionFilterChange,
  visibilityFilter,
  onVisibilityFilterChange,
  canFilterByVisibility = true,
}) => {
  const tComments = useTranslations('inspector.comments');
  const isAllSelected = !resolutionFilter && !visibilityFilter;

  return (
    <div className="flex items-center justify-between gap-x-3 border-t border-t-toolbar-bevel bg-ui-base px-3 py-1">
      {/* <TreeSearch
        value=""
        // value={commentsSearchValue}
        // onValueChange={setCommentsSearchInput}
        onValueChange={() => {}}
        onClear={() => {}}
        isLoading={commentsAreFetching}
        placeholder={tComments('searchPlaceholder')}
        toggleElement={
          <ToolbarAction
            icon={Search}
            aria-label={tComments('searchPlaceholder')}
            tooltip={tComments('searchPlaceholder')}
            disabled={comments.length === 0}
          />
        }
        disabled={comments.length === 0}
      /> */}
      {/* TODO: Remove this extra div once search is implemented */}
      <div></div>
      <div className="flex items-center gap-x-1">
        <DropdownMenu>
          <DropdownMenuTrigger data-cy="comment-filter-button" asChild>
            <ToolbarAction
              aria-label={tComments('filterComments')}
              disabled={comments.length === 0 && isAllSelected}
              tooltip={tComments('filterComments')}
              data-cy="comments-filter-button"
              className="p-3.5"
              iconClassName={cn(
                isAllSelected
                  ? 'stroke-icon-base! hover:stroke-gray-icon-hover-color!'
                  : 'stroke-highlight-base! hover:stroke-highlight-hover-color!',
              )}
              icon={ListFilter}
            />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" sideOffset={5.5}>
            <MenuProvider primitives={dropdownPrimitives}>
              <FilterCommentsMenu
                value={resolutionFilter}
                onValueChange={onResolutionFilterChange}
                visibilityValue={visibilityFilter}
                onVisibilityValueChange={onVisibilityFilterChange}
                canFilterByVisibility={canFilterByVisibility}
              />
            </MenuProvider>
          </DropdownMenuContent>
        </DropdownMenu>
        <DropdownMenu>
          <DropdownMenuTrigger data-cy="comment-options-button" asChild>
            <ToolbarAction
              icon={sortOrder === SortOrderOptions.ASC ? ArrowUpNarrowWide : ArrowDownNarrowWide}
              aria-label={tComments('sortComments')}
              disabled={comments.length === 0}
              tooltip={tComments('sortComments')}
              data-cy="comments-sort-button"
              className="p-3.5"
            />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" sideOffset={5.5}>
            <MenuProvider primitives={dropdownPrimitives}>
              <SortCommentsMenu
                value={sortOrder}
                onValueChange={onSortOrderChange}
                sortValue={sortValue}
                onSortValueChange={onSortValueChange}
              />
            </MenuProvider>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* <ToolbarAction
          icon={EllipsisVertical}
          aria-label={tComments('moreOptions')}
          disabled={comments.length === 0}
          tooltip={tComments('moreOptions')}
          data-cy="comments-options-button"
        /> */}
      </div>
    </div>
  );
};
